// @ts-nocheck
// TODO: align with current types (Phase 4a) — bucket 2/3 + DS strictness regressions
/**
 * Assign Role Modal
 *
 * Modal dialog for assigning a role to a user.
 *
 * The company comes first: roles are per-company records that share names
 * ("Administrator" exists once per tenant), so the role list only offers the
 * chosen company's own roles (via CompanyRoleSelect, the same picker as invite
 * and create-user). Listing every tenant's roles under identical names is how a
 * role from the wrong company got assigned; global roles are refused server-side.
 */

import React from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@lognai/synapse-design-system-v2';
import { Input } from '@lognai/synapse-design-system-v2';
import { Checkbox } from '@lognai/synapse-design-system-v2';
import { useToast } from '@lognai/synapse-design-system-v2';
import { CompanyCombobox, FormModal } from '../shared';
import { useAssignUserRole } from '../../hooks';
import { CompanyRoleSelect } from '../roles/CompanyRoleSelect';
import { useAssignmentCompany } from './use-assignment-company';

const assignRoleSchema = z.object({
  roleId: z.string().min(1, 'Role is required'),
  companyId: z.string().min(1, 'Company is required'),
  expiresAt: z.string().optional(),
  hasExpiry: z.boolean().default(false),
});

type AssignRoleFormData = z.infer<typeof assignRoleSchema>;

export interface AssignRoleModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
  userName?: string;
  onSuccess?: () => void;
}

export function AssignRoleModal({
  open,
  onOpenChange,
  userId,
  userName,
  onSuccess,
}: AssignRoleModalProps) {
  const { toast } = useToast();
  const assignRole = useAssignUserRole();

  const form = useForm<AssignRoleFormData>({
    resolver: zodResolver(assignRoleSchema),
    defaultValues: {
      roleId: '',
      companyId: '',
      expiresAt: '',
      hasExpiry: false,
    },
  });

  const hasExpiry = form.watch('hasExpiry');
  const companyId = form.watch('companyId');
  const { isPlatformAdmin } = useAssignmentCompany(form, userId, open);

  const handleSubmit = async (data: AssignRoleFormData) => {
    try {
      const assignment = {
        roleId: data.roleId,
        companyId: data.companyId,
        expiresAt: data.hasExpiry && data.expiresAt ? new Date(data.expiresAt) : undefined,
      };

      await assignRole.mutateAsync({ userId, assignment });

      toast({
        title: 'Role assigned',
        description: `Successfully assigned role to ${userName || 'user'}`,
      });

      form.reset();
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to assign role',
        variant: 'destructive',
      });
    }
  };

  return (
    <FormModal
      open={open}
      onOpenChange={onOpenChange}
      title="Assign Role"
      description={`Assign a role to ${userName || 'this user'}`}
      size="md"
      submitLabel="Assign Role"
      isSubmitting={assignRole.isPending}
      onSubmit={form.handleSubmit(handleSubmit)}
    >
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
          {/* Company */}
          <FormField
            control={form.control}
            name="companyId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Company *</FormLabel>
                <FormControl>
                  <CompanyCombobox
                    value={field.value}
                    onChange={(id) => field.onChange(id)}
                    disabled={!isPlatformAdmin}
                  />
                </FormControl>
                <FormMessage />
                <FormDescription>
                  The company this role applies in. Defaults to the user&apos;s company.
                </FormDescription>
              </FormItem>
            )}
          />

          {/* Role Selection */}
          <FormField
            control={form.control}
            name="roleId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Role *</FormLabel>
                <FormControl>
                  {companyId ? (
                    <CompanyRoleSelect
                      companyId={companyId}
                      value={field.value}
                      onChange={field.onChange}
                      preselectDefault={false}
                    />
                  ) : (
                    <p className="text-sm text-muted-foreground">Choose a company first</p>
                  )}
                </FormControl>
                <FormMessage />
                <FormDescription>
                  Roles defined for the selected company
                </FormDescription>
              </FormItem>
            )}
          />

          {/* Expiry Date Toggle */}
          <FormField
            control={form.control}
            name="hasExpiry"
            render={({ field }) => (
              <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                <FormControl>
                  <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                </FormControl>
                <div className="space-y-1 leading-none">
                  <FormLabel>Set expiry date</FormLabel>
                  <FormDescription>
                    Role will automatically expire after the specified date
                  </FormDescription>
                </div>
              </FormItem>
            )}
          />

          {/* Expiry Date Input */}
          {hasExpiry && (
            <FormField
              control={form.control}
              name="expiresAt"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Expiry Date *</FormLabel>
                  <FormControl>
                    <Input
                      type="date"
                      min={new Date().toISOString().split('T')[0]}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                  <FormDescription>
                    Role assignment will expire at the end of this date
                  </FormDescription>
                </FormItem>
              )}
            />
          )}
        </form>
      </Form>
    </FormModal>
  );
}